import {renderElement} from "../../utils/common";
import {getSelectedTagTemplate} from "./templates";

const FORM_CLASS_NAME = `js-tag-change`;
const CURRENT_LIST_CLASS_NAME = `tasks-filter__current-list`;
const INPUT_CLASS_NAME = `js-tag-input`;
const OPEN_BUTTON_CLASS_NAME = `js-tag-open`;
const MODAL_CLASS_NAME = `js-tag-modal`;
const BOTTOM_CLASS_NAME = `tasks-filter__bottom`;
const SELECT_CLASS_NAME = `task-filter__select`;
const ITEM_CLASS_NAME = `js-tag-item`;
const REMOVE_CLASS_NAME = `js-tag-change-remove`;
const ACTIVE_CLASS_NAME = `tag--active`;
const SHOW_CLASS_NAME = `modal--show`;

class TagChange {
  #formElement: HTMLFormElement | null;
  #currentListElement: HTMLElement | null;
  #hiddenInputElement: HTMLInputElement | null;
  #openButtonElement: HTMLButtonElement | null;
  #modalElement: HTMLElement | null;
  #bottomElement: HTMLElement | null;
  #selectElement: HTMLElement | null;
  #itemElements: NodeListOf<HTMLElement>;
  #selectedIds: Set<number>;

  constructor() {
    this.#formElement = document.querySelector(`.${FORM_CLASS_NAME}`);
    this.#itemElements = document.querySelectorAll(`.${ITEM_CLASS_NAME}`);
    this.#selectedIds = new Set<number>();

    if (this.#formElement) {
      this.#init();
    }
  }

  #init() {
    this.#currentListElement = this.#formElement!.querySelector(`.${CURRENT_LIST_CLASS_NAME}`);
    this.#hiddenInputElement = this.#formElement!.querySelector(`.${INPUT_CLASS_NAME}`);
    this.#openButtonElement = this.#formElement!.querySelector(`.${OPEN_BUTTON_CLASS_NAME}`);
    this.#modalElement = this.#formElement!.querySelector(`.${MODAL_CLASS_NAME}`);
    this.#bottomElement = this.#formElement!.querySelector(`.${BOTTOM_CLASS_NAME}`);
    this.#selectElement = this.#formElement!.querySelector(`.${SELECT_CLASS_NAME}`);

    this.#restoreSelectedItems();

    this.onTagItemClick = this.onTagItemClick.bind(this);
    this.onRemoveClick = this.onRemoveClick.bind(this);
    this.onOpenButtonClick = this.onOpenButtonClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);

    this.#itemElements.forEach((item) => item.addEventListener(`click`, this.onTagItemClick));

    if (this.#currentListElement) {
      this.#currentListElement.addEventListener(`click`, this.onRemoveClick);
    }

    if (this.#openButtonElement) {
      this.#openButtonElement.addEventListener(`click`, this.onOpenButtonClick);
    }
  }

  #restoreSelectedItems() {
    if (this.#hiddenInputElement) {
      const value = this.#hiddenInputElement.value;

      if (value) {
        const ids: number[] = JSON.parse(value);

        this.#selectedIds = new Set(ids);
      }
    }

    this.#syncSelectedItems();
    this.#renderSelectedTags();
    this.#updateBottomVisibility();
  }

  #syncSelectedItems() {
    this.#itemElements.forEach((item) => {
      const id = Number(item.dataset.id);

      if (this.#selectedIds.has(id)) {
        item.classList.add(ACTIVE_CLASS_NAME);
      } else {
        item.classList.remove(ACTIVE_CLASS_NAME);
      }
    });
  }

  #toggleItem(item: HTMLElement) {
    const id = Number(item.dataset.id);

    if (this.#selectedIds.has(id)) {
      this.#selectedIds.delete(id);
    } else {
      this.#selectedIds.add(id);
    }

    this.#syncSelectedItems();
    this.#renderSelectedTags();
    this.#updateBottomVisibility();
  }

  #renderSelectedTags() {
    if (!this.#currentListElement) {
      return;
    }

    this.#currentListElement.innerHTML = ``;

    this.#selectedIds.forEach((id) => {
      const itemElement = this.#getItemById(id);
      const name = itemElement ? itemElement.textContent!.trim() : id.toString();
      const color = itemElement ? itemElement.dataset.color! : ``;

      renderElement(this.#currentListElement!, getSelectedTagTemplate(id, name, color));
    });
  }

  #getItemById(id: number): HTMLElement | undefined {
    return Array.from(this.#itemElements).find((item) => Number(item.dataset.id) === id);
  }

  #updateBottomVisibility() {
    if (!this.#bottomElement) {
      return;
    }

    if (this.#selectedIds.size > 0) {
      this.#bottomElement.style.display = ``;
    } else {
      this.#bottomElement.style.display = `none`;
    }

    if (this.#hiddenInputElement) {
      this.#hiddenInputElement.value = JSON.stringify(Array.from(this.#selectedIds));
    }
  }

  onTagItemClick(evt: Event) {
    const item = evt.currentTarget as HTMLElement;

    this.#toggleItem(item);
  }

  onRemoveClick(evt: Event) {
    const target = evt.target as HTMLElement;
    const removeElement = target.closest(`.${REMOVE_CLASS_NAME}`);

    if (removeElement && this.#currentListElement!.contains(removeElement)) {
      evt.stopPropagation();

      this.#toggleItem(removeElement as HTMLElement);
    }
  }

  onOpenButtonClick() {
    this.#modalElement!.classList.toggle(SHOW_CLASS_NAME);

    if (this.#modalElement!.classList.contains(SHOW_CLASS_NAME)) {
      document.addEventListener(`click`, this.onDocumentClick);
    } else {
      document.removeEventListener(`click`, this.onDocumentClick);
    }
  }

  onDocumentClick(evt: Event) {
    const target = evt.target as HTMLElement;

    if (this.#selectElement && !target.closest(`.${SELECT_CLASS_NAME}`)) {
      this.#modalElement!.classList.remove(SHOW_CLASS_NAME);
      document.removeEventListener(`click`, this.onDocumentClick);
    }
  }
}

export {TagChange};
